/*
  Grove custom development
  Purpose: Drawer interactions for client-requested product page custom widgets.
  Date: 2026-07-17
  Developer: Codex
*/

(() => {
  const normalizeGroveHandle = (value) => (value || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  const escapeHtml = (value) => {
    const div = document.createElement("div");
    div.textContent = value || "";
    return div.innerHTML;
  };

  const formatMoney = (cents) => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: window.Shopify?.currency?.active || "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(cents / 100);
};

  const formatSearchMoney = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const amount = Number(String(value).replace(/[^0-9.-]/g, ""));
    if (!Number.isFinite(amount)) return "";
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: window.Shopify?.currency?.active || "GBP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat(document.documentElement.lang || "en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric"
    }).format(date);
  };

  const normalizeTrustpilotReviews = (payload) => {
    const reviews = Array.isArray(payload)
      ? payload
      : payload.reviews || payload.data?.reviews || payload.data || [];

    return reviews.map((review) => {
      const consumer = review.consumer || review.author || review.user || {};

      return {
        rating: Number(review.stars || review.rating || review.score || 5),
        title: review.title || review.headline || "",
        text: review.text || review.content || review.body || "",
        author: consumer.displayName || consumer.name || review.authorName || review.name || "",
        date: review.createdAt || review.created_at || review.date || review.publishedDate || ""
      };
    }).filter((review) => review.title || review.text);
  };

  const renderTrustpilotCard = (review) => {
    const rating = Math.max(1, Math.min(5, Math.round(review.rating || 5)));
    const stars = Array.from({ length: 5 }, (_, index) => {
      return `<span class="grove-trustpilot-card__star">${index < rating ? "★" : "☆"}</span>`;
    }).join("");

    return `
      <article class="grove-trustpilot-card">
        <div class="grove-trustpilot-card__stars" aria-label="${rating} out of 5 stars">${stars}</div>
        ${review.title ? `<h3 class="grove-trustpilot-card__title">${escapeHtml(review.title)}</h3>` : ""}
        ${review.text ? `<div class="grove-trustpilot-card__content"><p>${escapeHtml(review.text)}</p></div>` : ""}
        <div class="grove-trustpilot-card__footer">
          ${review.author ? `<div class="grove-trustpilot-card__author">${escapeHtml(review.author)}</div>` : ""}
          ${review.date ? `<div class="grove-trustpilot-card__date">${escapeHtml(formatDate(review.date))}</div>` : ""}
        </div>
      </article>
    `;
  };

  const normalizePredictiveResource = (resource) => ({
    title: resource.title || resource.text || "",
    url: resource.url || resource.styled_url || "#",
    image: resource.image || resource.featured_image || resource.featured_media?.preview_image || null,
    price: resource.price ?? resource.price_min ?? "",
    compareAtPrice: resource.compare_at_price ?? resource.compare_at_price_min ?? ""
  });

  const imageUrlFromPredictiveResource = (image) => {
    if (!image) return "";
    if (typeof image === "string") return image;
    return image.url || image.src || image.preview_image?.src || "";
  };

  const renderHeaderSearchProduct = (product) => {
    const item = normalizePredictiveResource(product);
    const image = imageUrlFromPredictiveResource(item.image);
    const price = formatSearchMoney(item.price);
    const compareAtPrice = formatSearchMoney(item.compareAtPrice);

    return `
      <a class="grove-search-card" href="${item.url}">
        ${image ? `<img class="grove-search-card__image" src="${image}" alt="${escapeHtml(item.title)}" loading="lazy">` : `<span class="grove-search-card__image"></span>`}
        <span class="grove-search-card__title">${escapeHtml(item.title)}</span>
        ${price ? `<span class="grove-search-card__price">${escapeHtml(price)}${compareAtPrice ? `<span class="grove-search-card__compare">${escapeHtml(compareAtPrice)}</span>` : ""}</span>` : ""}
      </a>
    `;
  };

  const renderHeaderSearchDropdown = (root, payload, query) => {
    const resultsTarget = root.querySelector("[data-grove-header-search-results]");
    const resources = payload.resources?.results || payload.resources || {};
    const products = (resources.products || []).slice(0, 8);
    const collections = (resources.collections || []).slice(0, 8);
    const searchUrl = `${window.Shopify?.routes?.root || "/"}search?type=product&q=${encodeURIComponent(query)}`;

    if (!resultsTarget) return;

    if (!products.length && !collections.length) {
      resultsTarget.innerHTML = `<div class="grove-search-results__empty">No results found for "${escapeHtml(query)}".</div>`;
      return;
    }

    resultsTarget.innerHTML = `
      <div class="grove-search-results">
        <aside class="grove-search-results__sidebar">
          <p class="grove-search-results__heading">Collections</p>
          <div class="grove-search-results__collections">
            ${collections.length ? collections.map((collection) => {
              const item = normalizePredictiveResource(collection);
              return `<a class="grove-search-results__collection" href="${item.url}">${escapeHtml(item.title)}</a>`;
            }).join("") : `<span class="grove-search-results__empty">No collections</span>`}
          </div>
        </aside>
        <div class="grove-search-results__main">
          <p class="grove-search-results__heading">Products</p>
          <div class="grove-search-results__products">
            ${products.map(renderHeaderSearchProduct).join("")}
          </div>
          <a class="grove-search-results__view-all" href="${searchUrl}">View all ${products.length} products <span aria-hidden="true">→</span></a>
        </div>
      </div>
    `;
  };

  const hydrateHeaderInstantSearch = () => {
    document.querySelectorAll(".custom-header-search").forEach((root) => {
      const form = root.querySelector("[data-grove-header-search]");
      const input = form?.querySelector('input[name="q"]');
      const searchButton = form?.querySelector(".custom-header-search__button");
      const dropdown = root.querySelector("[data-grove-header-search-dropdown]");
      const resultsTarget = root.querySelector("[data-grove-header-search-results]");
      const popularTemplate = root.querySelector("[data-grove-header-search-popular]");
      const closeButtons = root.querySelectorAll("[data-grove-header-search-close]");
      let abortController = null;
      let debounceTimer = null;

      if (!form || !input || !dropdown || !resultsTarget || root.hasAttribute("data-grove-instant-ready")) return;

      // Boost owns inputs explicitly connected to its search box. Do not also
      // attach Grove's focus/input handlers and open a second results panel.
      if (input.classList.contains("boost-pfs-search-box")) {
        dropdown.hidden = true;
        root.classList.remove("custom-header-search--open");

        // Boost V3 owns search: on mobile the input is visually hidden, so the icon must open Boost ISW
        const isMobile = () => window.matchMedia("(max-width: 999px)").matches;
        const fromButton = (event) => isMobile() && window.boostISWModule && event.target.closest(".custom-header-search__button");

        // Capture on the root so this runs before Boost's own click listener on the button,
        // which submits the form whenever the input already holds a query
        root.addEventListener("pointerdown", (event) => {
          // Keep the button from taking focus, otherwise :focus-within expands the form and the tap lands on the form
          if (fromButton(event)) event.preventDefault();
        }, true);
        root.addEventListener("click", (event) => {
          if (!fromButton(event)) return;
          event.preventDefault();
          event.stopPropagation();
          window.boostISWModule.openSuggestion(input);
        }, true);
        return;
      }
      root.setAttribute("data-grove-instant-ready", "true");

      const isMobileSearch = () => window.matchMedia("(max-width: 999px)").matches;

      const open = () => {
        const header = root.closest(".header, store-header, #shopify-section-header") || document.querySelector(".header");
        const headerBottom = header?.getBoundingClientRect().bottom || root.getBoundingClientRect().bottom;

        dropdown.style.setProperty("--grove-search-dropdown-top", `${Math.max(0, headerBottom)}px`);
        dropdown.hidden = false;
        root.classList.add("custom-header-search--open");
        document.documentElement.classList.toggle("grove-mobile-search-lock", isMobileSearch());
      };

      const close = () => {
        dropdown.hidden = true;
        root.classList.remove("custom-header-search--open");
        document.documentElement.classList.remove("grove-mobile-search-lock");
      };

      const fetchResults = () => {
        const query = input.value.trim();
        abortController?.abort();

        if (!query) {
          resultsTarget.innerHTML = popularTemplate?.innerHTML || `<div class="grove-search-results__loading">Start typing to search products.</div>`;
          open();
          return;
        }

        abortController?.abort();
        abortController = new AbortController();
        resultsTarget.innerHTML = `<div class="grove-search-results__loading">Searching...</div>`;
        open();

        const routeRoot = window.Shopify?.routes?.root || "/";
        const url = `${routeRoot}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=8&resources[options][fields]=title,product_type,variants.title,vendor`;

        fetch(url, { headers: { Accept: "application/json" }, signal: abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error("Search request failed");
            return response.json();
          })
          .then((payload) => {
            if (input.value.trim() === query) renderHeaderSearchDropdown(root, payload, query);
          })
          .catch((error) => {
            if (error.name === "AbortError") return;
            resultsTarget.innerHTML = `<div class="grove-search-results__empty">Search results are unavailable. Press enter to view the search page.</div>`;
          });
      };

      input.addEventListener("focus", fetchResults);
      input.addEventListener("input", () => {
        window.clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(fetchResults, 180);
      });

      const openMobileSearch = (event) => {
        if (!isMobileSearch() || root.classList.contains("custom-header-search--open")) return;

        event.preventDefault();
        event.stopPropagation();
        fetchResults();
        window.requestAnimationFrame(() => input.focus());
      };

      searchButton?.addEventListener("pointerdown", openMobileSearch);
      searchButton?.addEventListener("click", openMobileSearch);

      form.addEventListener("submit", (event) => {
        if (input.value.trim()) return;

        event.preventDefault();
        input.focus();
        fetchResults();
      });

      closeButtons.forEach((button) => {
        button.addEventListener("click", close);
      });

      document.addEventListener("click", (event) => {
        if (!root.contains(event.target)) close();
      });

      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") close();
      });
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", hydrateHeaderInstantSearch);
  } else {
    hydrateHeaderInstantSearch();
  }
  document.addEventListener("shopify:section:load", hydrateHeaderInstantSearch);

  const hydrateMobileNavigationDropdowns = () => {
    document.querySelectorAll("#header-sidebar-menu .grove-mobile-menu__dropdown").forEach((details) => {
      if (details.hasAttribute("data-grove-mobile-dropdown-ready")) return;
      details.setAttribute("data-grove-mobile-dropdown-ready", "true");

      const submenu = details.querySelector(":scope > .grove-mobile-menu__submenu");
      let scrollbar = null;
      let scrollbarThumb = null;

      const updateScrollbar = () => {
        if (!submenu || !scrollbar || !scrollbarThumb) return;

        const maxScroll = submenu.scrollHeight - submenu.clientHeight;
        const isScrollable = maxScroll > 4;
        const visibleRatio = submenu.clientHeight / submenu.scrollHeight;
        const thumbHeight = Math.max(visibleRatio * 100, 18);
        const trackHeight = scrollbar.clientHeight;
        const thumbPixelHeight = trackHeight * (thumbHeight / 100);
        const travel = Math.max(trackHeight - thumbPixelHeight, 0);
        const progress = isScrollable ? submenu.scrollTop / maxScroll : 0;

        details.classList.toggle("grove-mobile-menu__dropdown--scrollable", isScrollable);
        scrollbarThumb.style.height = `${isScrollable ? thumbHeight : 100}%`;
        scrollbarThumb.style.transform = `translateY(${travel * progress}px)`;
      };

      if (submenu) {
        scrollbar = document.createElement("span");
        scrollbar.className = "grove-mobile-menu__scrollbar";
        scrollbar.setAttribute("aria-hidden", "true");
        scrollbar.innerHTML = '<span class="grove-mobile-menu__scrollbar-thumb"></span>';
        details.appendChild(scrollbar);
        scrollbarThumb = scrollbar.querySelector(".grove-mobile-menu__scrollbar-thumb");

        submenu.addEventListener("scroll", updateScrollbar, { passive: true });
        window.addEventListener("resize", updateScrollbar);
        new MutationObserver(updateScrollbar).observe(submenu, { childList: true, subtree: true });
      }

      details.addEventListener("toggle", () => {
        if (!details.open) return;

        details.parentElement?.parentElement?.querySelectorAll(".grove-mobile-menu__dropdown[open]").forEach((openDetails) => {
          if (openDetails !== details) openDetails.open = false;
        });

        submenu?.scrollTo({ top: 0 });
        window.requestAnimationFrame(updateScrollbar);
      });

      updateScrollbar();
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", hydrateMobileNavigationDropdowns);
  } else {
    hydrateMobileNavigationDropdowns();
  }

  const hydrateTrustpilotSections = () => {
    document.querySelectorAll("[data-grove-trustpilot-section]").forEach((section) => {
      const url = section.getAttribute("data-reviews-url");
      const track = section.querySelector("[data-grove-trustpilot-track]");

      if (!url || !track) return;

      fetch(url, { headers: { Accept: "application/json" } })
        .then((response) => {
          if (!response.ok) throw new Error("Trustpilot review request failed");
          return response.json();
        })
        .then((payload) => {
          const maxReviews = Number(section.getAttribute("data-max-reviews") || 10);
          const reviews = normalizeTrustpilotReviews(payload).slice(0, maxReviews);

          if (reviews.length) {
            track.innerHTML = reviews.map(renderTrustpilotCard).join("");
            track.closest("scroll-carousel")?.classList.toggle("is-scrollable", reviews.length > 1);
          }
        })
        .catch(() => {});
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", hydrateTrustpilotSections);
  } else {
    hydrateTrustpilotSections();
  }

  const hydrateEstimatedDelivery = () => {
    const normalizeVendor = (value) => String(value || "").trim().toLowerCase();
    const ranges = new Map();
    let dayBasis = "calendar";
    let defaultRange = null;
    let deliveryDays = null;
    const parseRange = (value) => {
      if (!value) return null;
      const rawStart = String(value.start ?? "").trim();
      const rawEnd = String(value.end ?? "").trim();
      if (!/^\d+$/.test(rawStart) || !/^\d+$/.test(rawEnd)) return null;
      const start = Number(rawStart);
      const end = Number(rawEnd);
      return start >= 0 && end >= start && end <= 3650 ? { start, end } : null;
    };
    // The restored original settings take precedence over an obsolete draft section.
    const originalSources = document.querySelectorAll("[data-grove-original-vendor-delivery]");
    const sources = originalSources.length ? originalSources : document.querySelectorAll("[data-grove-vendor-delivery]");
    sources.forEach((source) => {
      try {
        const config = JSON.parse(source.textContent);
        dayBasis = config.day_basis || "calendar";
        const fallbackRange = config.default_range;
        if (fallbackRange?.enabled) defaultRange = parseRange(fallbackRange);
        if (config.roll_delivery_days && Array.isArray(config.delivery_days) && config.delivery_days.length === 7 && config.delivery_days.some((day) => day === true)) {
          deliveryDays = config.delivery_days;
        }
        (config.vendors || []).forEach((range) => {
          const vendor = normalizeVendor(range.vendor);
          const parsed = parseRange(range);
          if (vendor && parsed) ranges.set(vendor, parsed);
        });
      } catch (error) {
        console.warn("Grove delivery settings could not be read.");
      }
    });
    document.querySelectorAll("[data-grove-delivery-calendar]").forEach((source) => {
      try {
        const calendar = JSON.parse(source.textContent);
        if (Array.isArray(calendar.days) && calendar.days.length === 7 && calendar.days.every((day) => typeof day === "boolean")) {
          deliveryDays = calendar.days;
        }
      } catch (error) {
        console.warn("Grove delivery calendar could not be read.");
      }
    });
    const addDays = (days) => {
      const date = new Date();
      date.setHours(12, 0, 0, 0);
      for (let remaining = days; remaining > 0;) {
        date.setDate(date.getDate() + 1);
        if (dayBasis !== "weekdays" || (date.getDay() !== 0 && date.getDay() !== 6)) remaining--;
      }
      if (deliveryDays) {
        for (let offset = 0; offset < 7 && !deliveryDays[date.getDay()]; offset++) {
          date.setDate(date.getDate() + 1);
        }
      }
      return date;
    };
    const formatter = new Intl.DateTimeFormat(document.documentElement.lang || "en-GB", {
      weekday: "short", day: "numeric", month: "short"
    });
    document.querySelectorAll("[data-grove-estimated-delivery]").forEach((box) => {
      const range = deliveryDays && !deliveryDays.some(Boolean)
        ? null
        : ranges.get(normalizeVendor(box.dataset.deliveryVendor)) || defaultRange;
      const dates = box.querySelector("[data-grove-delivery-dates]");
      const fallback = box.querySelector("[data-grove-delivery-fallback]");
      const hasFallback = Boolean(fallback?.textContent.trim());
      if (dates) dates.hidden = !range;
      if (fallback) fallback.hidden = Boolean(range) || !hasFallback;
      box.hidden = !range && !hasFallback;
      if (!range) return;
      const start = box.querySelector("[data-grove-estimated-start]");
      const end = box.querySelector("[data-grove-estimated-end]");
      if (start) start.textContent = formatter.format(addDays(range.start));
      if (end) end.textContent = formatter.format(addDays(range.end));
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", hydrateEstimatedDelivery);
  } else {
    hydrateEstimatedDelivery();
  }
  window.addEventListener("load", hydrateEstimatedDelivery);
  document.addEventListener("shopify:section:load", hydrateEstimatedDelivery);
  document.documentElement.addEventListener("cart:refresh", hydrateEstimatedDelivery);
  document.documentElement.addEventListener("cart:change", hydrateEstimatedDelivery);
  // Cart and recommendation sections can arrive after the cart event fires.
  const deliveryObserver = new MutationObserver((records) => {
    if (records.some((record) => Array.from(record.addedNodes).some((node) =>
      node.nodeType === 1 && (node.matches?.("[data-grove-estimated-delivery], [data-grove-vendor-delivery], [data-grove-delivery-calendar]") ||
        node.querySelector?.("[data-grove-estimated-delivery], [data-grove-vendor-delivery], [data-grove-delivery-calendar]"))))) {
      hydrateEstimatedDelivery();
    }
  });
  deliveryObserver.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener("shopify:section:unload", () => requestAnimationFrame(hydrateEstimatedDelivery));


  const openDrawer = (drawer) => {
    if (!drawer) return;
    if (typeof drawer.show === "function") {
      drawer.show();
    } else {
      drawer.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("lock");
    }
  };

  const closeDrawer = (drawer) => {
    if (!drawer) return;
    if (typeof drawer.hide === "function") {
      return drawer.hide();
    } else {
      drawer.setAttribute("aria-hidden", "true");
      document.documentElement.classList.remove("lock");
    }
  };

  const cleanupDuplicateDrawers = (drawerId) => {
    if (!drawerId) return;

    const drawers = Array.from(document.querySelectorAll(`.grove-product-drawer[id="${CSS.escape(drawerId)}"]`));

    if (drawers.length <= 1) return;

    const keeper = drawers.find((drawer) => !drawer.parentElement?.matches("body")) || drawers[drawers.length - 1];

    drawers.forEach((drawer) => {
      if (drawer === keeper) return;

      if (drawer.open) {
        return;
      }

      if (typeof drawer.hide === "function") {
        drawer.hide();
      }

      drawer.remove();
    });
  };

  const getCurrentDrawer = (drawerId) => {
    cleanupDuplicateDrawers(drawerId);

    const drawers = Array.from(document.querySelectorAll(`.grove-product-drawer[id="${CSS.escape(drawerId)}"]`));

    return drawers.find((drawer) => !drawer.parentElement?.matches("body")) || drawers[0] || null;
  };

  const syncNativeVariantPicker = (variantChoice) => {
    const formId = variantChoice.getAttribute("data-product-form-id");
    const optionPosition = variantChoice.getAttribute("data-grove-option-position");
    const optionValueId = variantChoice.getAttribute("data-grove-option-value-id");
    const variantId = variantChoice.getAttribute("data-grove-variant-id");
    const form = document.getElementById(formId);

    if (!form) return false;

    const nativeOptionInput = optionPosition && optionValueId
      ? document.querySelector(`input[data-option-position="${optionPosition}"][form="${CSS.escape(formId)}"][value="${CSS.escape(optionValueId)}"]`)
      : null;

    if (nativeOptionInput) {
      nativeOptionInput.checked = true;
      nativeOptionInput.dispatchEvent(new Event("change", { bubbles: true }));
      return true;
    }

    const idInput = form.querySelector('input[name="id"]');

    if (idInput && variantId) {
      idInput.value = variantId;
      idInput.dispatchEvent(new Event("change", { bubbles: true }));
      return true;
    }

    return false;
  };

  const updateSizeActionText = (variantChoice) => {
    const drawer = variantChoice.closest(".grove-product-drawer");
    const selectedSize = variantChoice.getAttribute("data-grove-option-value-name");
    const trigger = drawer?.id ? document.querySelector(`[data-grove-size-action][data-grove-drawer-open="${CSS.escape(drawer.id)}"]`) : null;
    const label = trigger?.querySelector(".grove-product-action__label");

    if (!label || !selectedSize) return;

    label.textContent = selectedSize;
  };

  const updateFirmnessActionText = (variantChoice) => {
    const drawer = variantChoice.closest(".grove-product-drawer");
    const selectedFirmness = variantChoice.getAttribute("data-grove-option-value-name");
    const trigger = drawer?.id ? document.querySelector(`[data-grove-firmness-action][data-grove-drawer-open="${CSS.escape(drawer.id)}"]`) : null;
    const label = trigger?.querySelector(".grove-product-action__label");

    if (!label || !selectedFirmness) return;

    label.textContent = selectedFirmness;
  };

  const optionNameLooksLikeFirmness = (value) => {
    const text = (value || "").toLowerCase();

    return text.includes("firmness") || text.includes("tension") || text.includes("comfort") || text.includes("feel");
  };

  const normalizeFirmnessBadgeLabel = (value) => {
    const handle = normalizeGroveHandle(value);

    if (!handle || handle === "firmness" || handle === "orthopaedic" || handle === "orthopedic" || /^(choose|select|please-select)-/.test(handle)) return "";
    if (handle.includes("medium-firm")) return "Medium Firm";
    if (handle.includes("soft-medium")) return "Soft Medium";
    if (handle.includes("extra-firm")) return "Extra Firm";
    if (handle.includes("very-firm")) return "Very Firm";
    if (handle.includes("gentle")) return "Gentle";
    if (handle.includes("soft")) return "Soft";
    if (handle.includes("medium")) return "Medium";
    if (handle.includes("firm")) return "Firm";

    return "";
  };

  const getSelectedFirmnessText = () => {
    const firmnessActions = Array.from(document.querySelectorAll("[data-grove-firmness-action], [data-grove-variant-action]"));

    for (const action of firmnessActions) {
      const metaText = action.querySelector(".grove-product-action__meta")?.textContent?.trim().toLowerCase() || "";
      const optionName = action.getAttribute("data-grove-option-name")?.toLowerCase() || "";
      const labelText = action.querySelector(".grove-product-action__label")?.textContent?.trim();
      const actionLooksLikeFirmness = action.hasAttribute("data-grove-firmness-action")
        || metaText.includes("firmness")
        || metaText.includes("tension")
        || metaText.includes("comfort")
        || metaText.includes("feel")
        || optionName.includes("firmness")
        || optionName.includes("tension")
        || optionName.includes("comfort")
        || optionName.includes("feel");
      const customFirmness = normalizeFirmnessBadgeLabel(labelText);

      if (actionLooksLikeFirmness && customFirmness) return customFirmness;
    }

    for (const action of firmnessActions) {
      const customFirmness = normalizeFirmnessBadgeLabel(action.querySelector(".grove-product-action__label")?.textContent?.trim());
      if (customFirmness) return customFirmness;
    }

    const nativeOption = document.querySelector(".variant-picker__option.grove-native-firmness-option input:checked");
    const nativeLabel = nativeOption?.nextElementSibling?.querySelector(".sr-only")?.textContent?.trim()
      || nativeOption?.nextElementSibling?.textContent?.trim()
      || nativeOption?.value;

    return normalizeFirmnessBadgeLabel(nativeLabel);
  };

  const syncProductGalleryFirmnessBadge = (selectedFirmness = getSelectedFirmnessText()) => {
    const normalizedFirmness = normalizeFirmnessBadgeLabel(selectedFirmness);

    document.querySelectorAll(".grove-product-gallery-badges").forEach((badgeList) => {
      let badge = badgeList.querySelector(".grove-product-gallery-badge--firmness");

      if (!normalizedFirmness) {
        // Keep the server-rendered product-tag badge visible until a real selected firmness is available.
        return;
      }

      if (!badge) {
        badge = document.createElement("span");
        badge.className = "grove-product-gallery-badge grove-product-gallery-badge--firmness";
        badgeList.prepend(badge);
      }

      Array.from(badge.classList).forEach((className) => {
        if (className.startsWith("grove-product-gallery-badge--firmness-")) {
          badge.classList.remove(className);
        }
      });

      badge.hidden = false;
      badge.removeAttribute("hidden");
      badge.textContent = normalizedFirmness;
      badge.classList.add(`grove-product-gallery-badge--firmness-${normalizeGroveHandle(normalizedFirmness)}`);
    });
  };

  const syncProductGallerySaleBadge = (variant) => {
    if (!variant || typeof variant.price === "undefined") return;

    const isOnSale = Number(variant.compare_at_price || 0) > Number(variant.price || 0);

    document.querySelectorAll(".grove-product-gallery-badges").forEach((badgeList) => {
      let badge = badgeList.querySelector("[data-grove-gallery-sale-badge], .grove-product-gallery-badge--sale");
      const priceBadge = badge?.hasAttribute("data-grove-sale-from-price") ? badge : null;

      if (!isOnSale) {
        if (priceBadge) priceBadge.hidden = true;
        return;
      }

      if (!badge) {
        badge = document.createElement("span");
        badge.className = "grove-product-gallery-badge grove-product-gallery-badge--sale";
        badge.setAttribute("data-grove-gallery-sale-badge", "");
        badge.setAttribute("data-grove-sale-from-price", "");
        badge.textContent = "SALE";
        badgeList.prepend(badge);
      }

      badge.hidden = false;
      badge.removeAttribute("hidden");
      badge.textContent = "SALE";
    });
  };

  const updateVariantActionText = (variantChoice) => {
    const drawer = variantChoice.closest(".grove-product-drawer");
    const selectedValue = variantChoice.getAttribute("data-grove-option-value-name");
    const trigger = drawer?.id ? document.querySelector(`[data-grove-variant-action][data-grove-drawer-open="${CSS.escape(drawer.id)}"]`) : null;
    const label = trigger?.querySelector(".grove-product-action__label");

    if (!label || !selectedValue) return;

    label.textContent = selectedValue;
  };

  const splitPostcodeRules = (value) => {
    return (value || "")
      .split(",")
      .map((item) => item.trim().replace(/\s+/g, "").toUpperCase())
      .filter(Boolean);
  };

  const normalizePostcode = (value) => (value || "").trim().replace(/\s+/g, "").toUpperCase();

  const isValidUkPostcode = (value) => {
    const normalized = normalizePostcode(value);

    return /^[A-Z]{1,2}\d{1,2}[A-Z]?(?:\d[A-Z]{2})?$/.test(normalized);
  };

  const parsePostcodeDistrict = (value) => {
  let normalized = normalizePostcode(value);

  // If the last 3 characters are an inward postcode,
  // strip them before reading the postcode district.
  // EH4 3DG  -> EH43DG -> EH4
  // EH28 8SR -> EH288SR -> EH28
  if (/^\d[A-Z]{2}$/.test(normalized.slice(-3))) {
    normalized = normalized.slice(0, -3);
  }

  const match = normalized.match(/^([A-Z]{1,2})(\d{1,2})/);

  if (!match) return null;

  return {
    area: match[1],
    district: Number(match[2]),
    code: `${match[1]}${Number(match[2])}`
  };
};

  const postcodeMatchesRule = (postcode, rule) => {
    const normalizedRule = normalizePostcode(rule);
    const parsedPostcode = parsePostcodeDistrict(postcode);

    if (!normalizedRule) return false;

    if (normalizedRule.includes("-")) {
      const [start, end] = normalizedRule.split("-");
      const parsedStart = parsePostcodeDistrict(start);
      const parsedEnd = parsePostcodeDistrict(end);

      return Boolean(
        parsedPostcode &&
        parsedStart &&
        parsedEnd &&
        parsedPostcode.area === parsedStart.area &&
        parsedPostcode.area === parsedEnd.area &&
        parsedPostcode.district >= parsedStart.district &&
        parsedPostcode.district <= parsedEnd.district
      );
    }

    const parsedRule = parsePostcodeDistrict(normalizedRule);

    if (parsedRule && parsedPostcode) {
      return parsedPostcode.code === parsedRule.code;
    }

    return postcode.startsWith(normalizedRule);
  };

  const postcodeIsAvailable = (root, postcode) => {
    const normalizedPostcode = normalizePostcode(postcode);

    if (!isValidUkPostcode(normalizedPostcode)) return false;

    const rules = [
      ...splitPostcodeRules(root?.getAttribute("data-postcode-startup")),
      ...splitPostcodeRules(root?.getAttribute("data-postcode-ranges")),
      ...splitPostcodeRules(root?.getAttribute("data-available-postcodes"))
    ];

    return rules.length ? rules.some((rule) => postcodeMatchesRule(normalizedPostcode, rule)) : false;
  };

  const setServicesEnabled = (root, enabled) => {
    root?.setAttribute("data-postcode-valid", enabled ? "true" : "false");
    root?.querySelectorAll("[data-grove-service-checkbox]").forEach((checkbox) => {
      checkbox.disabled = !enabled;
      if (!enabled) checkbox.checked = false;
    });
  };

  const selectedServiceTotal = (drawer) => {
    return Array.from(drawer?.querySelectorAll("[data-grove-service-checkbox]:checked") || []).reduce((sum, input) => {
      return sum + Number(input.getAttribute("data-price") || 0);
    }, 0);
  };

  const selectedServiceTitles = (drawer) => {
    return Array.from(drawer?.querySelectorAll("[data-grove-service-checkbox]:checked") || [])
      .map((input) => input.getAttribute("data-title"))
      .filter(Boolean);
  };

  const updateServicesDrawerTotal = (drawer) => {
    const productPrice = Number(drawer?.getAttribute("data-product-price") || 0);
    const totalTarget = drawer?.querySelector("[data-grove-services-total]");

    if (totalTarget) {
      totalTarget.textContent = formatMoney(productPrice + selectedServiceTotal(drawer));
    }
  };

  const updateAdditionalServicesActionText = (drawer) => {
    const trigger = drawer?.id ? document.querySelector(`[data-grove-drawer-open="${CSS.escape(drawer.id)}"]`) : null;
    const meta = trigger?.querySelector(".grove-product-action__meta");
    const titles = selectedServiceTitles(drawer);

    if (!meta) return;

    if (!titles.length) {
      meta.textContent = trigger.getAttribute("data-default-meta") || meta.textContent;
      return;
    }

    meta.textContent = titles.length === 1 ? titles[0] : `${titles.length} selected`;
  };

  const getProductPriceElement = () => {
    return document.querySelector(".product-info sale-price, .product-info price-list sale-price, sale-price");
  };

  const updateDisplayedProductTotal = (drawer) => {
    const priceElement = getProductPriceElement();
    const basePrice = Number(drawer?.getAttribute("data-product-price") || 0);
    const serviceTotal = selectedServiceTotal(drawer);

    if (!priceElement || !basePrice) return;

    priceElement.textContent = formatMoney(basePrice + serviceTotal);
    priceElement.setAttribute("data-grove-service-adjusted", serviceTotal > 0 ? "true" : "false");
  };

  const resizeOpenDimensionAccordions = () => {
    document.querySelectorAll("[data-accordion-item].active").forEach((item) => {
      const content = item.querySelector("[class*='ai-accordion-content-']");
      if (content) content.style.maxHeight = `${content.scrollHeight}px`;
    });
  };

  const syncVariantDimensions = (variantId) => {
    const normalizedVariantId = variantId ? String(variantId) : "";

    document.querySelectorAll("[class*='ai-accordion-container-'], .product-info__accordion, .grove-metafield-accordions").forEach((scope) => {
      const dimensionItems = Array.from(scope.querySelectorAll("[data-grove-variant-dimensions]"));
      const fallbackItems = Array.from(scope.querySelectorAll(".grove-product-dimensions"));

      if (!dimensionItems.length) {
        fallbackItems.forEach((item) => {
          item.hidden = false;
        });
        return;
      }

      const selectedItem = dimensionItems.find((item) => item.getAttribute("data-grove-variant-dimensions") === normalizedVariantId);
      const visibleItem = selectedItem || null;

      dimensionItems.forEach((item) => {
        item.hidden = item !== visibleItem;
      });

      fallbackItems.forEach((item) => {
        item.hidden = Boolean(visibleItem);
      });
    });

    window.requestAnimationFrame(resizeOpenDimensionAccordions);
  };

  document.addEventListener("toggle", (event) => {
    const accordion = event.target;
    if (!accordion.matches?.(".grove-dimensions-accordion") || !accordion.open) return;
    window.setTimeout(() => {
      if (accordion.open) accordion.querySelector('summary')?.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 350);
  }, true);

  const getCurrentVariantId = () => {
    return document.querySelector('form[action*="/cart/add"] input[name="id"], product-form input[name="id"]')?.value || "";
  };

  const hydrateCollectionSeoReadMore = () => {
    document.querySelectorAll("[data-grove-collection-seo]").forEach((root) => {
      if (root.hasAttribute("data-grove-collection-seo-ready")) return;

      const content = root.querySelector("[data-grove-collection-seo-content]");
      const toggle = root.querySelector("[data-grove-collection-seo-toggle]");
      const readMore = root.querySelector("[data-grove-read-more]");
      const readLess = root.querySelector("[data-grove-read-less]");

      if (!content || !toggle) return;

      root.setAttribute("data-grove-collection-seo-ready", "true");

      const collapsedHeight = Number.parseFloat(getComputedStyle(root).getPropertyValue("--grove-collection-seo-collapsed-height")) || 116;
      const isMobileOnlyInactive = () => root.classList.contains("grove-collection-seo--mobile-only") && window.matchMedia("(min-width: 750px)").matches;
      const updateCollapsibleState = () => {
        if (isMobileOnlyInactive()) {
          root.classList.remove("is-collapsible", "is-expanded");
          toggle.setAttribute("aria-expanded", "false");
          if (readMore) readMore.hidden = false;
          if (readLess) readLess.hidden = true;
          return;
        }

        const wasExpanded = root.classList.contains("is-expanded");
        root.classList.add("is-expanded");
        const fullHeight = content.scrollHeight;
        root.classList.toggle("is-expanded", wasExpanded);
        const textLength = content.textContent.trim().length;
        root.classList.toggle("is-collapsible", fullHeight > collapsedHeight + 8 || textLength > 180);
      };

      toggle.addEventListener("click", () => {
        const expanded = !root.classList.contains("is-expanded");

        root.classList.toggle("is-expanded", expanded);
        toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
        if (readMore) readMore.hidden = expanded;
        if (readLess) readLess.hidden = !expanded;
      });

      updateCollapsibleState();
      window.addEventListener("resize", updateCollapsibleState);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", hydrateCollectionSeoReadMore);
  } else {
    hydrateCollectionSeoReadMore();
  }
  document.addEventListener("shopify:section:load", hydrateCollectionSeoReadMore);

  const hydrateNativeVariantPickerVisibility = () => {
    const hasRenderedSizeFlyout = Boolean(document.querySelector("[data-grove-size-action]"));
    const hasRenderedFirmnessFlyout = Boolean(document.querySelector("[data-grove-firmness-action]"));
    const renderedDynamicVariantPositions = new Set(
      Array.from(document.querySelectorAll("[data-grove-variant-action][data-grove-option-position]"))
        .map((item) => item.getAttribute("data-grove-option-position"))
        .filter(Boolean)
    );
    const hasRenderedDynamicVariantFlyouts = renderedDynamicVariantPositions.size > 0;
    document.documentElement.classList.toggle("grove-has-size-flyout", hasRenderedSizeFlyout);
    document.documentElement.classList.toggle("grove-has-firmness-flyout", hasRenderedFirmnessFlyout);
    document.documentElement.classList.toggle("grove-has-dynamic-variant-flyouts", hasRenderedDynamicVariantFlyouts);

    document.querySelectorAll('.product-info__block-item[data-block-type="variant-picker"]').forEach((block) => {
      const options = Array.from(block.querySelectorAll(".variant-picker__option"));
      options.forEach((option) => {
        const optionName = (option.getAttribute("data-grove-option-name") || "").toLowerCase();
        const optionText = (option.textContent || "").toLowerCase();
        const firstInput = option.querySelector("input[data-option-position]");
        const optionPosition = firstInput?.getAttribute("data-option-position");
        const isColourOption = optionName.includes("colour") || optionName.includes("color");
        option.classList.toggle("grove-native-dynamic-option", Boolean(optionPosition && renderedDynamicVariantPositions.has(optionPosition) && !isColourOption));
        option.classList.toggle("grove-native-size-option", optionName.includes("size"));
        option.classList.toggle(
          "grove-native-firmness-option",
          optionName.includes("firmness") ||
            optionName.includes("tension") ||
            optionName.includes("comfort") ||
            optionName.includes("feel") ||
            optionText.includes("gentle") ||
            optionText.includes("firm") ||
            optionText.includes("medium") ||
            optionText.includes("soft")
        );
      });

      const visibleOptions = options.filter((option) => {
        if (hasRenderedDynamicVariantFlyouts && option.classList.contains("grove-native-dynamic-option")) return false;
        if (hasRenderedSizeFlyout && option.classList.contains("grove-native-size-option")) return false;
        if (hasRenderedFirmnessFlyout && option.classList.contains("grove-native-firmness-option")) return false;
        return true;
      });

      block.classList.toggle("grove-native-variant-picker-empty", (hasRenderedDynamicVariantFlyouts || hasRenderedSizeFlyout || hasRenderedFirmnessFlyout) && options.length > 0 && visibleOptions.length === 0);
    });
  };

  const syncVariantOptionLabels = (scope = document) => {
    scope.querySelectorAll(".variant-picker").forEach((picker) => {
      picker.querySelectorAll("[data-grove-selected-option-value]").forEach((label) => {
        const optionPosition = label.getAttribute("data-grove-selected-option-value");
        const checkedInput = picker.querySelector(`input[data-option-position="${CSS.escape(optionPosition)}"]:checked`);
        const optionControl = checkedInput?.nextElementSibling;
        const selectedText = optionControl?.querySelector(".sr-only")?.textContent?.trim() || optionControl?.textContent?.trim();

        if (selectedText) {
          label.textContent = selectedText;
        }
      });
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      hydrateNativeVariantPickerVisibility();
      syncVariantOptionLabels();
    });
  } else {
    hydrateNativeVariantPickerVisibility();
    syncVariantOptionLabels();
  }
  document.addEventListener("shopify:section:load", (event) => {
    hydrateNativeVariantPickerVisibility();
    syncVariantOptionLabels(event.target);
  });
  document.addEventListener("product:rerender", () => {
    window.requestAnimationFrame(() => {
      hydrateNativeVariantPickerVisibility();
      syncVariantOptionLabels();
    });
  });

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-grove-drawer-open]");
    if (trigger) {
      event.preventDefault();
      openDrawer(getCurrentDrawer(trigger.getAttribute("data-grove-drawer-open")));
      return;
    }

    const close = event.target.closest("[data-grove-drawer-close]");
    if (close) {
      event.preventDefault();
      closeDrawer(close.closest(".grove-product-drawer, x-drawer"));
      return;
    }

    const variantChoice = event.target.closest("[data-grove-variant-id]");
    if (variantChoice) {
      const activeDrawer = variantChoice.closest(".grove-product-drawer, x-drawer");

      syncNativeVariantPicker(variantChoice);
      updateVariantActionText(variantChoice);
      updateSizeActionText(variantChoice);
      updateFirmnessActionText(variantChoice);
      if (optionNameLooksLikeFirmness(variantChoice.getAttribute("data-grove-option-name"))) {
        syncProductGalleryFirmnessBadge(variantChoice.getAttribute("data-grove-option-value-name"));
      } else {
        syncProductGalleryFirmnessBadge();
      }
      syncVariantDimensions(variantChoice.getAttribute("data-grove-variant-id"));

      variantChoice.closest(".grove-choice-list")?.querySelectorAll(".grove-choice").forEach((item) => item.classList.remove("is-selected"));
      variantChoice.classList.add("is-selected");
      closeDrawer(activeDrawer);

      window.setTimeout(() => cleanupDuplicateDrawers(activeDrawer?.id), 450);
      return;
    }

    const postcodeButton = event.target.closest("[data-grove-postcode-check]");
    if (postcodeButton) {
      event.preventDefault();
      const root = postcodeButton.closest("[data-grove-services]");
      const input = root?.querySelector("[data-grove-postcode-input]");
      const success = root?.querySelector("[data-grove-services-success]");
      const error = root?.querySelector("[data-grove-services-error]");
      const serviceList = root?.querySelector("[data-grove-service-list]");
      const postcode = input?.value.trim();
      const isAvailable = postcodeIsAvailable(root, postcode);

      success?.classList.remove("is-visible");
      error?.classList.remove("is-visible");

      if (isAvailable) {
        if (success) {
          success.textContent = success.getAttribute("data-message-template").replace("[Postcode]", postcode.toUpperCase());
          success.classList.add("is-visible");
        }
        if (serviceList) serviceList.hidden = false;
        setServicesEnabled(root, true);
        try {
          localStorage.setItem("user_postalcode", postcode.toUpperCase());
        } catch (error) {
          // Storage can fail in private browsing; postcode checking should still work.
        }
      } else {
        if (serviceList) serviceList.hidden = false;
        setServicesEnabled(root, false);
        if (error) {
          error.textContent = postcode ? (error.getAttribute("data-unavailable-message") || error.textContent) : (error.getAttribute("data-empty-message") || error.textContent);
          error.classList.add("is-visible");
        }
        updateServicesDrawerTotal(root?.closest(".grove-product-drawer, x-drawer"));
      }
      return;
    }

    const servicesAdd = event.target.closest("[data-grove-add-services]");
    if (servicesAdd) {
      event.preventDefault();
      if (servicesAdd.getAttribute("aria-busy") === "true") return;
      const drawer = servicesAdd.closest(".grove-product-drawer, x-drawer");
      const checked = drawer.querySelectorAll("[data-grove-service-checkbox]:checked");
      const items = Array.from(checked).map((input) => ({
        id: Number(input.value),
        quantity: 1
      }));

      if (!items.length) {
        updateAdditionalServicesActionText(drawer);
        updateDisplayedProductTotal(drawer);
        closeDrawer(drawer);
        return;
      }

      servicesAdd.setAttribute("aria-busy", "true");

      fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items })
      })
        .then(async (response) => {
          if (!response.ok) throw new Error("Unable to add services");
          updateAdditionalServicesActionText(drawer);
          updateDisplayedProductTotal(drawer);
          await closeDrawer(drawer);
          const cartDrawer = document.querySelector("cart-drawer");
          document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
          if (cartDrawer) {
            await customElements.whenDefined("cart-drawer");
            await cartDrawer.show();
          }
        })
        .catch(() => {
          const error = drawer.querySelector("[data-grove-services-error]");
          if (error) {
            error.textContent = error.getAttribute("data-add-error") || "Sorry, these services could not be added. Please try again.";
            error.classList.add("is-visible");
          }
        })
        .finally(() => servicesAdd.removeAttribute("aria-busy"));
    }
  });

  document.addEventListener("change", (event) => {
    if (event.target.matches(".variant-picker input[data-option-position]")) {
      syncVariantOptionLabels(event.target.closest(".variant-picker") || document);
    }

    if (event.target.matches('form[action*="/cart/add"] input[name="id"], product-form input[name="id"]')) {
      syncVariantDimensions(event.target.value);
      return;
    }

    if (!event.target.matches("[data-grove-service-checkbox]")) return;

    const drawer = event.target.closest(".grove-product-drawer, x-drawer");
    updateServicesDrawerTotal(drawer);
  });

  const hydrateServiceAvailabilityState = () => {
    document.querySelectorAll("[data-grove-services]").forEach((root) => {
      setServicesEnabled(root, root.getAttribute("data-postcode-valid") === "true");
    });
  };

  const hydrateLinkStacksScrollbars = () => {
    document.querySelectorAll(".link-stacks-container").forEach((container) => {
      if (container.hasAttribute("data-grove-link-stacks-ready")) {
        return;
      }

      container.setAttribute("data-grove-link-stacks-ready", "true");

      const scrollbar = document.createElement("div");
      scrollbar.className = "grove-link-stacks-scrollbar";
      scrollbar.setAttribute("aria-hidden", "true");
      scrollbar.innerHTML = '<span class="grove-link-stacks-scrollbar__thumb"></span>';
      container.appendChild(scrollbar);

      const thumb = scrollbar.querySelector(".grove-link-stacks-scrollbar__thumb");
      let scrollArea = null;

      const getScrollableArea = () => {
        const preferred = [
          ".link-stacks-list",
          ".link-stacks-items",
          ".link-stacks-wrapper",
          ".link-stacks-grid"
        ];

        for (const selector of preferred) {
          const element = container.querySelector(selector);

          if (element && element.scrollWidth > element.clientWidth + 4) {
            return element;
          }
        }

        return [container, ...container.querySelectorAll("*")]
          .find((element) => element.scrollWidth > element.clientWidth + 4) || null;
      };

      const updateScrollbar = () => {
        const nextScrollArea = getScrollableArea();

        if (nextScrollArea !== scrollArea) {
          scrollArea?.removeEventListener("scroll", updateScrollbar);
          scrollArea = nextScrollArea;
          scrollArea?.addEventListener("scroll", updateScrollbar, { passive: true });
        }

        if (!scrollArea || !thumb) {
          container.classList.remove("grove-link-stacks-scrollable");
          return;
        }

        const maxScroll = scrollArea.scrollWidth - scrollArea.clientWidth;
        const isScrollable = maxScroll > 4;
        const visibleRatio = scrollArea.clientWidth / scrollArea.scrollWidth;
        const progress = isScrollable ? scrollArea.scrollLeft / maxScroll : 0;
        const progressWidth = isScrollable
          ? Math.min(100, Math.max(visibleRatio * 100, (visibleRatio + progress * (1 - visibleRatio)) * 100))
          : 100;

        container.classList.toggle("grove-link-stacks-scrollable", isScrollable);
        thumb.style.width = `${progressWidth}%`;
        thumb.style.transform = "";
      };

      new MutationObserver(updateScrollbar).observe(container, { childList: true, subtree: true });
      window.addEventListener("resize", updateScrollbar);
      window.requestAnimationFrame(updateScrollbar);
      window.setTimeout(updateScrollbar, 600);
      window.setTimeout(updateScrollbar, 1600);
    });
  };

  const normalizeOptionText = (value) => (value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");

  const selectVariantOptionFromGalleryAlt = (gallery, altText) => {
    const match = (altText || "").match(/#([^_#]+)_(.+)$/);

    if (!match) return;

    const optionName = normalizeOptionText(match[1]);
    const optionValue = normalizeOptionText(match[2]);
    const formId = gallery.getAttribute("form") || "";
    const picker = document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"]`) || document.querySelector("variant-picker");
    const option = Array.from(picker?.querySelectorAll(".variant-picker__option") || []).find((item) => {
      return normalizeOptionText(item.getAttribute("data-grove-option-name")) === optionName;
    });

    if (!option) return;

    const matchingInput = Array.from(option.querySelectorAll("input[data-option-position]")).find((input) => {
      const label = option.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      const labelText = label?.querySelector(".sr-only")?.textContent || label?.textContent || "";

      return normalizeOptionText(labelText) === optionValue;
    });

    if (matchingInput && !matchingInput.checked) {
      matchingInput.checked = true;
      matchingInput.dispatchEvent(new Event("change", { bubbles: true }));
    }
  };

  const syncVariantOptionFromGallerySlide = (gallery, slide) => {
    const image = slide?.querySelector("img");

    if (!image) return;

    selectVariantOptionFromGalleryAlt(gallery, image.getAttribute("alt"));
  };

  const updateGalleryMobileCount = (gallery, index) => {
    const current = gallery.querySelector("[data-grove-gallery-current]");
    const total = gallery.querySelector("[data-grove-gallery-total]");

    if (!current || !total) return;

    const slides = Array.from(gallery.querySelectorAll(".product-gallery__media:not([hidden])"));
    const fallbackIndex = slides.findIndex((slide) => slide.classList.contains("is-selected"));
    const nextIndex = Number.isInteger(index) ? index : fallbackIndex;

    current.textContent = String(Math.max(1, nextIndex + 1));
    total.textContent = String(slides.length || total.textContent || 1);
  };

  const hydrateProductThumbnailSliders = () => {
    document.querySelectorAll(".product-gallery__thumbnail-list-wrapper").forEach((wrapper) => {
      if (wrapper.hasAttribute("data-grove-thumbnail-slider-ready")) return;

      const list = wrapper.querySelector(".product-gallery__thumbnail-list");
      const prev = wrapper.querySelector("[data-grove-thumbnail-prev]");
      const next = wrapper.querySelector("[data-grove-thumbnail-next]");
      const gallery = wrapper.closest("product-gallery");

      if (!list || !prev || !next || !gallery) return;

      wrapper.setAttribute("data-grove-thumbnail-slider-ready", "true");

      const updateButtons = () => {
        const items = Array.from(list.querySelectorAll(".product-gallery__thumbnail:not([hidden])"));
        const selectedIndex = items.findIndex((item) => item.getAttribute("aria-current") === "true");
        const fillsThumbnailRail = items.length >= 5;
        const hasMoreThanFive = items.length > 5;

        wrapper.setAttribute("data-grove-thumbnail-overflow", fillsThumbnailRail ? "true" : "false");
        prev.disabled = !hasMoreThanFive || selectedIndex <= 0;
        next.disabled = !hasMoreThanFive || selectedIndex === -1 || selectedIndex >= items.length - 1;
      };

      const selectAdjacentThumbnail = (direction) => {
        const items = Array.from(list.querySelectorAll(".product-gallery__thumbnail:not([hidden])"));
        const selectedIndex = items.findIndex((item) => item.getAttribute("aria-current") === "true");
        const nextIndex = Math.min(items.length - 1, Math.max(0, selectedIndex + direction));

        if (nextIndex === selectedIndex || !items[nextIndex]) return;

        if (typeof list.select === "function") {
          list.select(nextIndex);
        } else {
          items[nextIndex].click();
        }

        window.setTimeout(updateButtons, 250);
      };

      prev.addEventListener("click", () => selectAdjacentThumbnail(-1));

      next.addEventListener("click", () => selectAdjacentThumbnail(1));

      list.addEventListener("click", (event) => {
        const thumbnail = event.target.closest(".product-gallery__thumbnail");
        const image = thumbnail?.querySelector("img");

        if (!thumbnail || !image) return;

        window.requestAnimationFrame(() => selectVariantOptionFromGalleryAlt(gallery, image.getAttribute("alt")));
      });

      gallery.addEventListener("carousel:change", (event) => {
        window.requestAnimationFrame(() => {
          syncVariantOptionFromGallerySlide(gallery, event.detail?.slide);
          updateGalleryMobileCount(gallery, event.detail?.index);
          updateButtons();
        });
      });

      list.addEventListener("scroll", updateButtons, { passive: true });
      window.addEventListener("resize", updateButtons);
      window.requestAnimationFrame(() => {
        updateButtons();
        updateGalleryMobileCount(gallery);
      });
      window.setTimeout(updateButtons, 600);
    });
  };

  const getCollectionCardMeta = () => {
    const script = document.querySelector('script[id^="grove-collection-card-meta-"]');

    if (!script) return {};

    try {
      return JSON.parse(script.textContent || "{}");
    } catch (error) {
      return {};
    }
  };

  const getProductHandleFromUrl = (url) => {
    if (!url) return "";

    try {
      const parsedUrl = new URL(url, window.location.origin);
      const match = parsedUrl.pathname.match(/\/products\/([^/?#]+)/);
      return match?.[1] || "";
    } catch (error) {
      const match = url.match(/\/products\/([^/?#]+)/);
      return match?.[1] || "";
    }
  };

  const groveProductMetaCache = new Map();
  const groveFirmnessHandles = new Set(["gentle", "soft", "medium", "firm", "medium-firm", "soft-medium", "extra-firm", "very-firm"]);
  const groveHandleize = normalizeGroveHandle;

  const getFirmnessLabelFromTag = (tag) => {
    const handle = groveHandleize(tag);

    if (groveFirmnessHandles.has(handle)) {
      return handle.replace(/-/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
    }

    if (handle.includes("orthop")) {
      if (handle.includes("medium-firm")) return "Medium Firm";
      if (handle.includes("soft-medium")) return "Soft Medium";
      if (handle.includes("extra-firm")) return "Extra Firm";
      if (handle.includes("very-firm")) return "Very Firm";
      if (handle.includes("gentle")) return "Gentle";
      if (handle.includes("soft")) return "Soft";
      if (handle.includes("medium")) return "Medium";
      if (handle.includes("firm")) return "Firm";
      return "";
    }

    if (!handle.includes("firmness")) return "";

    const label = handle
      .replace("mattress-firmness-rating-", "")
      .replace("mattress-firmness-", "")
      .replace("firmness-rating-", "")
      .replace("firmness-", "")
      .replace(/-/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());

    const normalizedLabel = groveHandleize(label);

    if (normalizedLabel.includes("medium-firm")) return "Medium Firm";
    if (normalizedLabel.includes("soft-medium")) return "Soft Medium";
    if (normalizedLabel.includes("extra-firm")) return "Extra Firm";
    if (normalizedLabel.includes("very-firm")) return "Very Firm";
    if (normalizedLabel.includes("gentle")) return "Gentle";
    if (normalizedLabel.includes("soft")) return "Soft";
    if (normalizedLabel.includes("medium")) return "Medium";
    if (normalizedLabel.includes("firm")) return "Firm";

    return label && label !== "Firmness" && groveFirmnessHandles.has(normalizedLabel) ? label : "";
  };

  const normalizeProductTags = (tags) => {
    if (Array.isArray(tags)) return tags;
    if (typeof tags === "string") return tags.split(",").map((tag) => tag.trim()).filter(Boolean);
    return [];
  };

  const productHasCompareAtPrice = (product) => {
    if (!product) return false;

    if (Number(product.compare_at_price || 0) > Number(product.price || 0)) return true;

    return Array.isArray(product.variants) && product.variants.some((variant) => {
      return Number(variant.compare_at_price || 0) > Number(variant.price || 0);
    });
  };

  const buildProductTagBadges = (tags, product) => {
    const handles = tags.map(groveHandleize);
    const badges = [];
    const addBadge = (handle, text, className) => {
      if (!badges.some((badge) => badge.handle === handle)) badges.push({ handle, text, className });
    };
    const hasExpressDeliveryTag = handles.some((handle) => {
      return handle === "express-delivery"
        || handle === "express-dispatch"
        || handle === "next-day-delivery"
        || handle === "next-day-dispatch"
        || (handle.includes("express") && (handle.includes("delivery") || handle.includes("dispatch")))
        || (handle.includes("next-day") && (handle.includes("delivery") || handle.includes("dispatch")));
    });

    if (hasExpressDeliveryTag) {
      addBadge("express-delivery", "EXPRESS DELIVERY", "badge--grove-express");
    }

    if ((handles.includes("sale") || handles.includes("on-sale")) && !productHasCompareAtPrice(product)) {
      addBadge("sale", "SALE", "badge--on-sale");
    }

    if (handles.some((handle) => ["new", "new-arrival", "new-arrivals"].includes(handle))) {
      addBadge("new", "NEW", "badge--grove-new");
    }

    if (handles.includes("clearance")) {
      addBadge("clearance", "CLEARANCE", "badge--grove-clearance");
    }

    if (handles.includes("flash-sale") || handles.includes("flashsale")) {
      addBadge("flash-sale", "FLASH SALE", "badge--grove-flash");
    }

    return badges;
  };

  const buildProductMetaFromJson = (product) => {
    const tags = normalizeProductTags(product?.tags);
    const firmness = [];

    tags.forEach((tag) => {
      const label = getFirmnessLabelFromTag(tag);
      if (label && !firmness.includes(label)) firmness.push(label);
    });

    const productType = normalizeOptionText(product?.type || product?.product_type || "");
    const productTitle = normalizeOptionText(product?.title || "");
    const optionDefinitions = Array.isArray(product?.options_with_values) ? product.options_with_values : (Array.isArray(product?.options) ? product.options : []);
    const firmnessOptionIndex = optionDefinitions.findIndex((option) => {
      const optionName = typeof option === "string" ? option : option?.name;
      return groveHandleize(optionName || "").includes("firmness");
    });
    const hasFirmnessOption = firmnessOptionIndex >= 0;
    const firstAvailableVariant = product?.variants?.find((variant) => variant.available) || product?.variants?.[0];
    if (hasFirmnessOption && firstAvailableVariant) {
      const optionPosition = Number(optionDefinitions[firmnessOptionIndex]?.position) || firmnessOptionIndex + 1;
      const firmnessValue = firstAvailableVariant[`option${optionPosition}`] || firstAvailableVariant.options?.[firmnessOptionIndex] || "";
      const firmnessLabel = getFirmnessLabelFromTag(firmnessValue) || (groveFirmnessHandles.has(groveHandleize(firmnessValue)) ? groveHandleize(firmnessValue).replace(/-/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()) : "");
      if (firmnessLabel && !firmness.includes(firmnessLabel)) firmness.push(firmnessLabel);
    }
    const isMattress = hasFirmnessOption || productType.includes("mattress") || productTitle.includes("mattress") || tags.some((tag) => {
      const handle = groveHandleize(tag);
      return handle.includes("mattress") || Boolean(getFirmnessLabelFromTag(tag));
    });

    return {
      vendor: product?.vendor || "",
      firmness: firmness.join("/"),
      isMattress,
      badges: buildProductTagBadges(tags, product)
    };
  };

  const fetchProductMeta = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (groveProductMetaCache.has(handle)) return groveProductMetaCache.get(handle);

    const routeRoot = window.Shopify?.routes?.root || "/";
    const promise = fetch(`${routeRoot}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error("Product metadata request failed");
        return response.json();
      })
      .then(buildProductMetaFromJson)
      .catch(() => null);

    groveProductMetaCache.set(handle, promise);
    return promise;
  };

  const renderBoostMattressCardMeta = (card, meta) => {
    if (!meta) return false;

    if (!meta.isMattress && !meta.firmness) {
      card.querySelectorAll(".grove-product-card-meta--boost, .grove-product-card-meta__firmness--mobile").forEach((element) => element.remove());
      return false;
    }

    const info = card.querySelector(".boost-sd__product-info, .boost-pfs-filter-product-bottom, .boost-pfs-filter-product-item-info, .spf-product-card__info");
    const title = card.querySelector(".boost-sd__product-title, .boost-pfs-filter-product-item-title, .spf-product-card__title");
    const vendor = card.querySelector(".boost-sd__product-vendor, .boost-pfs-filter-product-item-vendor, .spf-product-card__vendor")
      || Array.from(card.querySelectorAll("[class]")).find((element) => {
        const className = element.className.toString().toLowerCase();
        return className.includes("vendor") || className.includes("brand");
      });

    if (!info || !title) return false;

    const ensureMobileFirmness = () => {
      if (!meta.firmness || info.querySelector(".grove-product-card-meta__firmness--mobile")) return;

      const mobileFirmness = document.createElement("span");
      mobileFirmness.className = `grove-product-card-meta__firmness grove-product-card-meta__firmness--mobile grove-product-card-meta__firmness--${groveHandleize(meta.firmness)}`;
      mobileFirmness.textContent = meta.firmness;
      title.insertAdjacentElement("afterend", mobileFirmness);
    };

    let row = card.querySelector(".grove-product-card-meta--boost");

    if (row) {
      if (meta.isMattress && meta.vendor && !row.querySelector(".grove-product-card-meta__vendor")) {
        const vendorElement = document.createElement("span");
        vendorElement.className = "grove-product-card-meta__vendor";
        vendorElement.textContent = meta.vendor;
        row.prepend(vendorElement);
      }

      if (meta.firmness && !row.querySelector(".grove-product-card-meta__firmness")) {
        const firmness = document.createElement("span");
        firmness.className = `grove-product-card-meta__firmness grove-product-card-meta__firmness--${groveHandleize(meta.firmness)}`;
        firmness.textContent = meta.firmness;
        row.appendChild(firmness);
      }

      ensureMobileFirmness();
      return true;
    }

    row = document.createElement("div");
    row.className = "grove-product-card-meta grove-product-card-meta--boost";

    if (meta.isMattress && vendor) {
      vendor.classList.add("grove-product-card-meta__vendor");
      row.appendChild(vendor);
    } else if (meta.isMattress && meta.vendor) {
      const vendorElement = document.createElement("span");
      vendorElement.className = "grove-product-card-meta__vendor";
      vendorElement.textContent = meta.vendor;
      row.appendChild(vendorElement);
    }

    if (meta.firmness) {
      const firmness = document.createElement("span");
      firmness.className = `grove-product-card-meta__firmness grove-product-card-meta__firmness--${groveHandleize(meta.firmness)}`;
      firmness.textContent = meta.firmness;
      row.appendChild(firmness);
    }

    if (!row.childElementCount) return false;

    info.insertBefore(row, title);
    ensureMobileFirmness();
    return true;
  };

  const renderBoostProductBadges = (card, badges) => {
    if (!Array.isArray(badges) || badges.length === 0) return false;

    const imageWrap = card.querySelector(".boost-sd__product-image-wrapper, .boost-pfs-filter-product-item-image, .spf-product-card__image-wrapper, .gf-product-image, .boost-sd__product-image");
    if (!imageWrap) return false;

    let badgeList = imageWrap.querySelector(".grove-boost-product-badge-list");

    if (!badgeList) {
      badgeList = document.createElement("div");
      badgeList.className = "grove-boost-product-badge-list";
      imageWrap.appendChild(badgeList);
    }

    imageWrap.querySelectorAll('[class*="label"], [class*="badge"]').forEach((element) => {
      if (element.closest(".grove-boost-product-badge-list")) return;

      const label = (element.textContent || "").trim();
      if (!/^(sale|on sale|new|clearance|express delivery)$/i.test(label)) return;

      element.classList.add("badge");
      if (/^sale$|^on sale$/i.test(label)) element.classList.add("badge--on-sale");
      if (/^new$/i.test(label)) element.classList.add("badge--grove-new");
      if (/^clearance$/i.test(label)) element.classList.add("badge--grove-clearance");
      if (/^express delivery$/i.test(label)) element.classList.add("badge--grove-express");
      badgeList.appendChild(element);
    });

    let rendered = false;

    badges.forEach((badge) => {
      if (!badge?.handle || badgeList.querySelector(`[data-grove-badge-handle="${badge.handle}"]`)) return;
      if (badge.handle === "sale" && /(^|\s)(sale|on sale)(\s|$)/i.test(card.textContent || "")) return;

      const badgeElement = document.createElement("span");
      badgeElement.className = `badge badge--custom badge--grove-tag ${badge.className || ""}`.trim();
      badgeElement.dataset.groveBadgeHandle = badge.handle;
      badgeElement.textContent = badge.text;
      badgeList.appendChild(badgeElement);
      rendered = true;
    });

    return rendered || badgeList.childElementCount > 0;
  };

  const renderBoostProductCardEnhancements = (card, meta) => {
    const renderedMeta = renderBoostMattressCardMeta(card, meta);
    const renderedBadges = renderBoostProductBadges(card, meta?.badges);

    return renderedMeta || renderedBadges;
  };

  const hydrateBoostMattressCards = () => {
    const metaByHandle = getCollectionCardMeta();

    document.querySelectorAll(".boost-sd__product-item, .boost-pfs-filter-product-item, .spf-product-card").forEach((card) => {
      const link = card.querySelector('a[href*="/products/"]');
      const handle = getProductHandleFromUrl(link?.getAttribute("href"));
      const liquidMeta = metaByHandle[handle];

      if (!handle || card.hasAttribute("data-grove-boost-card-loading")) return;

      if (card.hasAttribute("data-grove-boost-card-ready")) {
        const metaRow = card.querySelector(".grove-product-card-meta--boost");
        const expectedFirmness = card.getAttribute("data-grove-card-firmness") || "";
        const expectedVendor = card.getAttribute("data-grove-card-vendor") || "";
        const renderedFirmness = metaRow?.querySelector(".grove-product-card-meta__firmness")?.textContent.trim() || "";
        const renderedVendor = metaRow?.querySelector(".grove-product-card-meta__vendor")?.textContent.trim() || "";
        const hasMobileFirmness = !expectedFirmness || Boolean(card.querySelector(".grove-product-card-meta__firmness--mobile"));
        if (metaRow && renderedFirmness === expectedFirmness && renderedVendor === expectedVendor && hasMobileFirmness) return;
        card.removeAttribute("data-grove-boost-card-ready");
      }

      if (liquidMeta) renderBoostMattressCardMeta(card, { ...liquidMeta, isMattress: true });

      card.setAttribute("data-grove-boost-card-loading", "true");
      fetchProductMeta(handle).then((meta) => {
        card.removeAttribute("data-grove-boost-card-loading");
        const resolvedMeta = meta && liquidMeta
          ? { ...meta, ...liquidMeta, vendor: liquidMeta.vendor || meta.vendor, firmness: liquidMeta.firmness || meta.firmness, isMattress: true }
          : meta;

        if (resolvedMeta) {
          card.setAttribute("data-grove-card-firmness", resolvedMeta.firmness || "");
          card.setAttribute("data-grove-card-vendor", resolvedMeta.isMattress ? resolvedMeta.vendor || "" : "");
        }
        if (renderBoostProductCardEnhancements(card, resolvedMeta)) {
          card.setAttribute("data-grove-boost-card-ready", "true");
        }
      });
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      hydrateServiceAvailabilityState();
      hydrateLinkStacksScrollbars();
      hydrateProductThumbnailSliders();
      hydrateBoostMattressCards();
    });
  } else {
    hydrateServiceAvailabilityState();
    hydrateLinkStacksScrollbars();
    hydrateProductThumbnailSliders();
    hydrateBoostMattressCards();
  }

  window.addEventListener("load", hydrateLinkStacksScrollbars);
  window.addEventListener("load", hydrateProductThumbnailSliders);
  window.addEventListener("load", hydrateBoostMattressCards);
  document.addEventListener("shopify:section:load", hydrateLinkStacksScrollbars);
  document.addEventListener("shopify:section:load", hydrateProductThumbnailSliders);
  document.addEventListener("shopify:section:load", hydrateBoostMattressCards);

  if (document.body) {
    new MutationObserver(() => {
      window.clearTimeout(window.groveBoostMattressCardsTimer);
      window.groveBoostMattressCardsTimer = window.setTimeout(hydrateBoostMattressCards, 80);
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener("variant:change", (event) => {
    const variantPrice = event.detail?.variant?.price;
    const variantId = event.detail?.variant?.id;

    if (variantPrice) {
      document.querySelectorAll(".grove-product-drawer[data-product-price]").forEach((drawer) => {
        drawer.setAttribute("data-product-price", variantPrice);
        updateServicesDrawerTotal(drawer);
        updateDisplayedProductTotal(drawer);
      });
    }

    syncVariantDimensions(variantId);
    window.requestAnimationFrame(() => {
      hydrateNativeVariantPickerVisibility();
      syncProductGallerySaleBadge(event.detail?.variant);
      syncProductGalleryFirmnessBadge();
    });
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      syncVariantDimensions(getCurrentVariantId());
      syncProductGalleryFirmnessBadge();
    });
  } else {
    syncVariantDimensions(getCurrentVariantId());
    syncProductGalleryFirmnessBadge();
  }
})();
